import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUsers, FiPlus, FiEdit2, FiTrash2, FiLogOut, FiChevronDown,
  FiChevronUp, FiX, FiSave, FiPackage, FiSearch, FiShield, FiMessageSquare
} from 'react-icons/fi';
import AdminLogin from './AdminLogin';

const API = process.env.REACT_APP_API_BASE_URL || '';

const SERVICOS = [
  { value: 'sintia',  label: 'PapIA' },
  { value: 'lucrum',  label: 'Lucrum' },
  { value: 'delivex', label: 'Delivex' },
  { value: 'rotas',   label: 'Rotas' },
];
const STATUS_OPTIONS = ['pendente', 'pago', 'atrasado'];

const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style:'currency', currency:'BRL' }).format(v);

// ─────────────────────────────────────────────────
//  Modal genérico
// ─────────────────────────────────────────────────
const Modal = ({ open, onClose, title, children }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          className="bg-gray-800 rounded-2xl border border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b border-gray-700">
            <h2 className="text-lg font-bold text-white">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1"><FiX /></button>
          </div>
          <div className="p-5">{children}</div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─────────────────────────────────────────────────
//  Form Assinante
// ─────────────────────────────────────────────────
const emptyAssinante = { cpf:'',nome:'',email:'',telefone:'',empresa:'',endereco:'',cidade:'',estado:'',contrato:'' };

const AssinanteForm = ({ initial, onSave, onCancel, loading }) => {
  const [f, setF] = useState(initial || emptyAssinante);
  const set = (k,v) => setF(p => ({...p,[k]:v}));
  const fields = [
    { key:'cpf', label:'CPF/CNPJ', ph:'CPF ou CNPJ', required:true },
    { key:'nome', label:'Nome', ph:'Nome completo', required:true },
    { key:'contrato', label:'Nº Contrato', ph:'Ex: 450000', required:false },
    { key:'email', label:'E-mail', ph:'email@exemplo.com' },
    { key:'telefone', label:'Telefone', ph:'(41) 99999-0000' },
    { key:'empresa', label:'Empresa', ph:'Nome da empresa' },
    { key:'endereco', label:'Endereço', ph:'Rua, número, bairro' },
    { key:'cidade', label:'Cidade', ph:'Curitiba' },
    { key:'estado', label:'Estado', ph:'PR' },
  ];
  return (
    <div className="space-y-4">
      {fields.map(({key,label,ph,required}) => (
        <div key={key}>
          <label className="text-sm text-gray-400 mb-1 block">{label}{required && ' *'}</label>
          <input value={f[key]||''} onChange={e=>set(key,e.target.value)} placeholder={ph}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm" />
        </div>
      ))}
      <div className="flex gap-3 pt-2">
        <button onClick={()=>onSave(f)} disabled={loading}
          className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
          {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> : <><FiSave className="w-4 h-4"/> Salvar</>}
        </button>
        <button onClick={onCancel} className="flex-1 py-2 border border-gray-600 text-gray-300 hover:bg-gray-700 rounded-lg text-sm">Cancelar</button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
//  Form Plano
// ─────────────────────────────────────────────────
const emptyPlano = { servico:'sintia', valor_mensal:'', plano_nome:'', contrato:'', data_vencimento:'', status_pagamento:'pendente', ativo:true, observacoes:'' };

const PlanoForm = ({ initial, onSave, onCancel, loading }) => {
  const [f, setF] = useState(initial || emptyPlano);
  const set = (k,v) => setF(p => ({...p,[k]:v}));
  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Serviço *</label>
        <select value={f.servico} onChange={e=>set('servico',e.target.value)}
          className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none text-sm">
          {SERVICOS.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm text-gray-400 mb-1 block">Nome do plano</label>
          <input value={f.plano_nome||''} onChange={e=>set('plano_nome',e.target.value)} placeholder="Ex: PapIA Pro"
            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm" />
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-1 block">Nº Contrato</label>
          <input value={f.contrato||''} onChange={e=>set('contrato',e.target.value)} placeholder="Ex: 450000"
            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm text-gray-400 mb-1 block">Valor mensal (R$) *</label>
          <input type="number" step="0.01" value={f.valor_mensal||''} onChange={e=>set('valor_mensal',e.target.value)} placeholder="89.90"
            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm" />
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-1 block">Vencimento</label>
          <input type="date" value={f.data_vencimento||''} onChange={e=>set('data_vencimento',e.target.value)}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm text-gray-400 mb-1 block">Status pgto.</label>
          <select value={f.status_pagamento} onChange={e=>set('status_pagamento',e.target.value)}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none text-sm">
            {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" checked={f.ativo} onChange={e=>set('ativo',e.target.checked)} className="w-4 h-4 accent-blue-500" />
            Ativo
          </label>
        </div>
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Observações</label>
        <textarea value={f.observacoes||''} onChange={e=>set('observacoes',e.target.value)} rows={2}
          className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm resize-none" />
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={()=>onSave(f)} disabled={loading}
          className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
          {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> : <><FiSave className="w-4 h-4"/> Salvar</>}
        </button>
        <button onClick={onCancel} className="flex-1 py-2 border border-gray-600 text-gray-300 hover:bg-gray-700 rounded-lg text-sm">Cancelar</button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
//  Form Admin
// ─────────────────────────────────────────────────
const AdminForm = ({ onSave, onCancel, loading }) => {
  const [f, setF] = useState({ nome:'', login:'', senha:'' });
  const set = (k,v) => setF(p => ({...p,[k]:v}));
  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Nome *</label>
        <input value={f.nome} onChange={e=>set('nome',e.target.value)} placeholder="Nome do administrador"
          className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm" />
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Login *</label>
        <input value={f.login} onChange={e=>set('login',e.target.value)} placeholder="Nome de usuário"
          className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm" />
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Senha * <span className="text-gray-500">(mín. 6 caracteres)</span></label>
        <input type="password" value={f.senha} onChange={e=>set('senha',e.target.value)} placeholder="••••••"
          className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm" />
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={()=>onSave(f)} disabled={loading}
          className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
          {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> : <><FiSave className="w-4 h-4"/> Criar Admin</>}
        </button>
        <button onClick={onCancel} className="flex-1 py-2 border border-gray-600 text-gray-300 hover:bg-gray-700 rounded-lg text-sm">Cancelar</button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
//  Painel Admin principal
// ─────────────────────────────────────────────────
const AdminPanel = () => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const [activeTab, setActiveTab] = useState('assinantes'); // 'assinantes' | 'admins'

  // Assinantes state
  const [assinantes, setAssinantes] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState(null);

  // Assinante modal state
  const [showAssinanteModal, setShowAssinanteModal] = useState(false);
  const [editingAssinante, setEditingAssinante] = useState(null);
  const [showPlanoModal, setShowPlanoModal] = useState(false);
  const [editingPlano, setEditingPlano] = useState(null);
  const [planoAssinanteId, setPlanoAssinanteId] = useState(null);

  // Admins state
  const [admins, setAdmins] = useState([]);
  const [showAdminModal, setShowAdminModal] = useState(false);

  // WhatsApp templates state
  const [showWppModal, setShowWppModal] = useState(false);
  const [wppTemplates, setWppTemplates] = useState({ msg2dias: '', msgHoje: '' });
  const [wppLoading, setWppLoading] = useState(false);

  const headers = useCallback((tkn) => ({ 'Content-Type':'application/json', Authorization:`Bearer ${tkn || token}` }), [token]);

  // Validar token existente ao montar
  useEffect(() => {
    if (!token) return;
    // Decode JWT to check if expired (without network call)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        // Token expirado
        localStorage.removeItem('admin_token');
        setToken(null);
        return;
      }
    } catch {
      localStorage.removeItem('admin_token');
      setToken(null);
      return;
    }
    // Token parece válido, verificar com o backend
    setLoading(true);
    fetch(`${API}/api/admin/assinantes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        // Extrair admin info do JWT
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setAdmin({ id: payload.sub, login: payload.login });
        } catch { setAdmin({}); }
        setAssinantes(data.assinantes || []);
      })
      .catch(() => { localStorage.removeItem('admin_token'); setToken(null); })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const fetchAssinantes = useCallback(async (tkn) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/assinantes`, { headers: headers(tkn) });
      const data = await res.json();
      if (data.success) setAssinantes(data.assinantes);
    } catch { setError('Erro ao carregar.'); }
    finally { setLoading(false); }
  }, [headers]);

  const fetchPlanos = useCallback(async (assinanteId, tkn) => {
    try {
      const res = await fetch(`${API}/api/admin/assinantes/${assinanteId}/planos`, { headers: headers(tkn) });
      const data = await res.json();
      if (data.success) setPlanos(data.planos);
    } catch { /* ignore */ }
  }, [headers]);

  const fetchAdmins = useCallback(async (tkn) => {
    try {
      const res = await fetch(`${API}/api/admin/admins`, { headers: headers(tkn) });
      const data = await res.json();
      if (data.success) setAdmins(data.admins);
    } catch { /* ignore */ }
  }, [headers]);

  const handleLogin = (adminData, tkn) => {
    setAdmin(adminData);
    setToken(tkn);
    // Use token directly since React hasn't batched the state update yet
    fetchAssinantes(tkn);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdmin(null);
    setToken(null);
    setAssinantes([]);
    setAdmins([]);
  };

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); setPlanos([]); return; }
    setExpanded(id);
    await fetchPlanos(id);
  };

  // Switch tab
  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'admins' && admins.length === 0) fetchAdmins();
  };

  // ── CRUD Assinante ──────────────────────
  const saveAssinante = async (formData) => {
    setModalLoading(true);
    try {
      const isEdit = !!editingAssinante;
      const url = isEdit ? `${API}/api/admin/assinantes/${editingAssinante.id}` : `${API}/api/admin/assinantes`;
      const res = await fetch(url, { method: isEdit ? 'PUT' : 'POST', headers: headers(), body: JSON.stringify(formData) });
      const data = await res.json();
      if (!res.ok) { alert(data.message); return; }
      setShowAssinanteModal(false);
      setEditingAssinante(null);
      fetchAssinantes();
    } catch { alert('Erro de conexão.'); }
    finally { setModalLoading(false); }
  };

  const deleteAssinante = async (id) => {
    if (!window.confirm('Excluir assinante e todos os seus planos?')) return;
    try {
      await fetch(`${API}/api/admin/assinantes/${id}`, { method: 'DELETE', headers: headers() });
      fetchAssinantes();
      if (expanded === id) { setExpanded(null); setPlanos([]); }
    } catch { alert('Erro.'); }
  };

  // ── CRUD Plano ──────────────────────────
  const savePlano = async (formData) => {
    setModalLoading(true);
    try {
      const isEdit = !!editingPlano;
      const payload = { ...formData, assinante_id: planoAssinanteId };
      const url = isEdit ? `${API}/api/admin/planos/${editingPlano.id}` : `${API}/api/admin/planos`;
      const res = await fetch(url, { method: isEdit ? 'PUT' : 'POST', headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) { alert(data.message); return; }
      setShowPlanoModal(false);
      setEditingPlano(null);
      fetchPlanos(planoAssinanteId);
      fetchAssinantes();
    } catch { alert('Erro de conexão.'); }
    finally { setModalLoading(false); }
  };

  const deletePlano = async (planoId, assinanteId) => {
    if (!window.confirm('Excluir este plano?')) return;
    try {
      await fetch(`${API}/api/admin/planos/${planoId}`, { method: 'DELETE', headers: headers() });
      fetchPlanos(assinanteId);
      fetchAssinantes();
    } catch { alert('Erro.'); }
  };

  // ── CRUD Admin ──────────────────────────
  const saveAdmin = async (formData) => {
    setModalLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/admins`, { method: 'POST', headers: headers(), body: JSON.stringify(formData) });
      const data = await res.json();
      if (!res.ok) { alert(data.message); return; }
      setShowAdminModal(false);
      fetchAdmins();
    } catch { alert('Erro de conexão.'); }
    finally { setModalLoading(false); }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm('Excluir este administrador?')) return;
    try {
      const res = await fetch(`${API}/api/admin/admins/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) { alert(data.message); return; }
      fetchAdmins();
    } catch { alert('Erro.'); }
  };

  const fetchWppTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/admin/whatsapp/templates`, { headers: headers() });
      const data = await res.json();
      if (data.success) setWppTemplates(data.templates);
    } catch { /* silently ignore */ }
  };

  const saveWppTemplates = async () => {
    setWppLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/whatsapp/templates`, {
        method: 'PUT', headers: headers(), body: JSON.stringify(wppTemplates),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.message); return; }
      setShowWppModal(false);
    } catch { alert('Erro de conexão.'); }
    finally { setWppLoading(false); }
  };

  // ── Se não logado → tela de login ──────
  if (!admin || !token) return <AdminLogin onLogin={handleLogin} />;

  // ── Filtro ─────────────────────────────
  const filtered = assinantes.filter(a => {
    const q = search.toLowerCase();
    return !q || a.nome.toLowerCase().includes(q) || a.cpf.includes(q) || (a.empresa||'').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-20">
        <div>
          <h1 className="text-xl font-bold">AyTech <span className="text-blue-400">Admin</span></h1>
          <p className="text-xs text-gray-400">{assinantes.length} assinante{assinantes.length!==1?'s':''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { fetchWppTemplates(); setShowWppModal(true); }}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-green-400 transition-colors px-3 py-2 rounded-lg hover:bg-gray-700"
            title="Editar mensagens WhatsApp">
            <FiMessageSquare className="w-4 h-4" /> WhatsApp
          </button>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-gray-400 hover:text-red-400 transition-colors px-3 py-2 rounded-lg hover:bg-gray-700">
            <FiLogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
        {error && <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-xl text-sm">{error}</div>}

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-800 rounded-xl p-1 border border-gray-700">
          <button onClick={() => switchTab('assinantes')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'assinantes' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}>
            <FiUsers className="w-4 h-4" /> Assinantes
          </button>
          <button onClick={() => switchTab('admins')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'admins' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}>
            <FiShield className="w-4 h-4" /> Administradores
          </button>
        </div>

        {/* ═══ TAB: ASSINANTES ═══ */}
        {activeTab === 'assinantes' && (
          <>
            {/* Search + Add */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" />
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar por nome, CPF/CNPJ ou empresa..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm" />
              </div>
              <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}}
                onClick={()=>{ setEditingAssinante(null); setShowAssinanteModal(true); }}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap">
                <FiPlus className="w-4 h-4" /> Novo Assinante
              </motion.button>
            </div>

            {/* Lista */}
            {loading && assinantes.length === 0 ? (
              <div className="text-center py-16 text-gray-500">Carregando...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <FiUsers className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>{search ? 'Nenhum resultado.' : 'Nenhum assinante cadastrado.'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map(a => (
                  <motion.div key={a.id} layout className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
                    {/* Row */}
                    <div className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-750"
                      onClick={() => toggleExpand(a.id)}>
                      <div className="w-10 h-10 bg-blue-600/20 rounded-full flex items-center justify-center text-blue-400 font-bold text-sm shrink-0">
                        {a.nome.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{a.nome}</p>
                        <p className="text-xs text-gray-400">{a.cpf} · {a.empresa || '—'} · {a.cidade||''}{a.estado? `/${a.estado}`:''}</p>
                      </div>
                      <div className="hidden sm:flex items-center gap-4">
                        <span className="text-xs bg-blue-600/20 text-blue-300 px-2 py-1 rounded-lg">{a.planos_ativos} plano{a.planos_ativos!==1?'s':''}</span>
                        <span className="text-sm font-semibold text-green-400">{formatCurrency(a.total_mensal)}/mês</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={e=>{e.stopPropagation();setEditingAssinante(a);setShowAssinanteModal(true);}}
                          className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-900/30 rounded-lg transition-colors" title="Editar">
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button onClick={e=>{e.stopPropagation();deleteAssinante(a.id);}}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-900/30 rounded-lg transition-colors" title="Excluir">
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                        {expanded === a.id ? <FiChevronUp className="w-5 h-5 text-gray-500" /> : <FiChevronDown className="w-5 h-5 text-gray-500" />}
                      </div>
                    </div>

                    {/* Planos expandidos */}
                    <AnimatePresence>
                      {expanded === a.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-gray-700 bg-gray-850 overflow-hidden"
                        >
                          <div className="p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                                <FiPackage className="w-4 h-4" /> Planos
                              </h4>
                              <button onClick={()=>{setPlanoAssinanteId(a.id);setEditingPlano(null);setShowPlanoModal(true);}}
                                className="flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-semibold">
                                <FiPlus className="w-3 h-3" /> Add Plano
                              </button>
                            </div>

                            {planos.length === 0 ? (
                              <p className="text-sm text-gray-500 text-center py-4">Nenhum plano cadastrado.</p>
                            ) : (
                              <div className="space-y-2">
                                {planos.map(p => (
                                  <div key={p.id} className="flex items-center gap-3 bg-gray-900/60 rounded-lg px-4 py-3">
                                    <span className={`w-2 h-2 rounded-full ${p.ativo ? 'bg-green-400' : 'bg-gray-600'}`} />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-white font-medium">{SERVICOS.find(s=>s.value===p.servico)?.label||p.servico}{p.plano_nome ? ` — ${p.plano_nome}`:''}</p>
                                      <p className="text-xs text-gray-400">
                                        {formatCurrency(p.valor_mensal)}/mês · {p.status_pagamento} · vence {p.data_vencimento || '—'}
                                      </p>
                                    </div>
                                    <button onClick={()=>{setPlanoAssinanteId(a.id);setEditingPlano(p);setShowPlanoModal(true);}}
                                      className="p-1.5 text-gray-400 hover:text-blue-400 rounded" title="Editar"><FiEdit2 className="w-3.5 h-3.5" /></button>
                                    <button onClick={()=>deletePlano(p.id,a.id)}
                                      className="p-1.5 text-gray-400 hover:text-red-400 rounded" title="Excluir"><FiTrash2 className="w-3.5 h-3.5" /></button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ═══ TAB: ADMINISTRADORES ═══ */}
        {activeTab === 'admins' && (
          <>
            <div className="flex justify-end">
              <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}}
                onClick={()=>setShowAdminModal(true)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap">
                <FiPlus className="w-4 h-4" /> Novo Admin
              </motion.button>
            </div>

            {admins.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <FiShield className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Carregando administradores...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {admins.map(a => (
                  <div key={a.id} className="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4 flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-600/20 rounded-full flex items-center justify-center text-purple-400 shrink-0">
                      <FiShield className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{a.nome}</p>
                      <p className="text-xs text-gray-400">Login: <span className="text-gray-300 font-mono">{a.login}</span> · Criado em {new Date(a.criado_em).toLocaleDateString('pt-BR')}</p>
                    </div>
                    <button onClick={()=>deleteAdmin(a.id)}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-900/30 rounded-lg transition-colors" title="Excluir admin">
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Modal Assinante */}
      <Modal open={showAssinanteModal} onClose={()=>{setShowAssinanteModal(false);setEditingAssinante(null);}}
        title={editingAssinante ? 'Editar Assinante' : 'Novo Assinante'}>
        <AssinanteForm initial={editingAssinante} loading={modalLoading} onSave={saveAssinante} onCancel={()=>{setShowAssinanteModal(false);setEditingAssinante(null);}} />
      </Modal>

      {/* Modal Plano */}
      <Modal open={showPlanoModal} onClose={()=>{setShowPlanoModal(false);setEditingPlano(null);}}
        title={editingPlano ? 'Editar Plano' : 'Novo Plano'}>
        <PlanoForm initial={editingPlano} loading={modalLoading} onSave={savePlano} onCancel={()=>{setShowPlanoModal(false);setEditingPlano(null);}} />
      </Modal>

      {/* Modal Novo Admin */}
      <Modal open={showAdminModal} onClose={()=>setShowAdminModal(false)} title="Novo Administrador">
        <AdminForm loading={modalLoading} onSave={saveAdmin} onCancel={()=>setShowAdminModal(false)} />
      </Modal>

      {/* Modal WhatsApp Templates */}
      <Modal open={showWppModal} onClose={()=>setShowWppModal(false)} title="Mensagens WhatsApp">
        <div className="space-y-5">
          <p className="text-xs text-gray-400">
            Variáveis disponíveis: <span className="font-mono text-green-400">{'{nome}'}</span>, <span className="font-mono text-green-400">{'{planoNome}'}</span>, <span className="font-mono text-green-400">{'{valor}'}</span>, <span className="font-mono text-green-400">{'{vencimento}'}</span>
          </p>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Mensagem — 2 dias antes do vencimento</label>
            <textarea
              rows={6}
              value={wppTemplates.msg2dias}
              onChange={e => setWppTemplates(p => ({ ...p, msg2dias: e.target.value }))}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-green-500 focus:outline-none text-sm resize-y font-mono"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Mensagem — no dia do vencimento</label>
            <textarea
              rows={6}
              value={wppTemplates.msgHoje}
              onChange={e => setWppTemplates(p => ({ ...p, msgHoje: e.target.value }))}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-green-500 focus:outline-none text-sm resize-y font-mono"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={() => setShowWppModal(false)}
              className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold text-sm">
              Cancelar
            </button>
            <button onClick={saveWppTemplates} disabled={wppLoading}
              className="flex-1 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
              {wppLoading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> : <><FiSave className="w-4 h-4"/> Salvar</>}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanel;
